import type { SubmitHandler, FieldValues, DefaultValues, Path } from "react-hook-form";
import type { FormEvent } from "react";
import type { AxiosError, FormFieldParamsInterface } from "@follosoft/react-components";
import type { PressEvent } from "@react-types/shared";

import { useForm } from "react-hook-form";
import { useState } from "react";
import { axiosInstance } from "@follosoft/react-components";

interface ValidationErrorResponseInterface {
  message: string;
  errors: {
    [key: string]: string[];
  };
}

type UseFormRequestOptions<TFormData extends FieldValues> = {
  /** Convert data right before axios sends it (e.g. to FormData). */
  transform?: (data: TFormData) => any; // TFormData | FormData
  isMultipart?: boolean;
};

const useFormRequest = <TFormData extends FieldValues, TResponseData>(
  url: string,
  method: "POST" | "GET" | "PUT" | "DELETE" = "POST",
  defaultValues?: DefaultValues<TFormData>,
  successCallback?: (resultData: TResponseData, data: TFormData) => void | null,
  errorCallback?: (error: AxiosError, data: TFormData) => void | null,
  options?: UseFormRequestOptions<TFormData>,
) => {
  const form = useForm<TFormData>({
    defaultValues,
    shouldUnregister: false,
  });

  const {
    setValue,
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = form;

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<TResponseData | null>(null);
  const [backendError, setBackendError] = useState<AxiosError<{ message: string }> | null>(null);

  const onSubmit: SubmitHandler<TFormData> = async (data) => {
    setLoading(true);
    setResponse(null);
    setBackendError(null);

    try {
      const payload = options?.transform ? options.transform(data) : data;

       const result = await axiosInstance()<TResponseData>({
        method,
        url,
        data: payload,
        headers: options?.isMultipart
          ? {
              "Content-Type": "multipart/form-data",
            }
          : undefined,
      });

      setResponse(result.data);
      reset();
      if (successCallback) {
        successCallback(result.data, data);
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;

      setBackendError(axiosError);

      if (errorCallback) {
        errorCallback(axiosError, data);
      }

      if (axiosError.response) {
        if (axiosError.response.status === 422) {
          const validationErrorResponse = axiosError.response.data as ValidationErrorResponseInterface;

          Object.entries(validationErrorResponse.errors ?? {}).forEach(([field, errorMessages]) => {
            // A refusal (ApiException) lists JSON:API error objects, not messages per field:
            // there is no field to pin it on, and it is errorCallback's to report.
            if (!Array.isArray(errorMessages)) return;

            errorMessages.forEach((errorMessage: string) => {
              setError(field as Path<TFormData>, {
                type: "manual",
                message: errorMessage,
              });
            });
          });
        }
      } else {
        //TODO add error
      }
    } finally {
      setLoading(false);
    }
  };

  const send = async (event?: FormEvent | PressEvent, data?: TFormData) => {
    //TODO fix this later
    // @ts-ignore
    if (event && typeof event.preventDefault !== "undefined") event.preventDefault();
    clearErrors();

    if (data) {
      await onSubmit(data);
    } else {
      await handleSubmit(onSubmit)();
    }
  };

  return {
    form,
    setValue,
    reset,
    formFieldParams: {
      reset,
      setValue,
      register,
      errors,
      loading,
      watch,
    } as FormFieldParamsInterface<TFormData>,
    handleSubmit,
    onSubmit,
    loading,
    response,
    send,
    backendError,
  };
};

export default useFormRequest;
